/* eslint-disable no-shadow */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { theme } from 'fpay-ui';
import { useAuth } from '@hooks';

import { getEnv } from '@config';

const { publicPath } = getEnv();

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 20px;
  margin-right: 20px;
  margin-right: 21px;
  margin-top: 0px;
  align-items: center;
`;

const ModalImage = styled.img(
  ({ theme }) => css`
    width: 140px;
    height: 120px;
    margin-top: 0px;
    ${theme.breakpoint('ll')} {
      width: 182px;
      height: 152px;
      margin-top: 0px;
    }
  `
);

const ModalTextHeader = styled.div(
  ({ theme }) => css`
    ${theme.textTypes['Title-w400']}
    line-height: 1.22;
    text-align: center;
    margin-top: 4px;
    color: ${theme.colors.gray800};
    ${theme.breakpoint('ll')} {
      margin-top: 4px;
    }
  `
);

const Line = styled.div(
  ({ theme }) => css`
    border-top: 1px solid ${theme.colors.gray400};
    width: 100%;
    margin-top: 26px;
  `
);

const ModalTextBody = styled.div(
  ({ theme }) => css`
    ${theme.textTypes['Subtitle-1-w400']}
    line-height: 1.5;
    text-align: center;
    margin-top: 23px;
    margin-bottom: 24px;
    color: ${theme.colors.gray800};
  `
);

const ModalButtonQuestions = styled.div`
  ${theme.textTypes['Body-2-w400']}
  text-align: center;
  width: 100%;
  max-width: 274;
  line-height: normal;
  color: ${theme.colors.white};
  width: 100%;
  padding-top: 12px;
  padding-bottom: 11px;
  justify-content: center;
  margin-bottom: 16px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${theme.colors.gbrand500};
  transition: all 0.3s ease 0s;
  &:hover {
    box-shadow: 0 1px 6px rgb(32 33 36 / 28%);
    background-color: ${theme.colors.gbrand600};
  }
  &:active {
    border: 1px solid ${theme.colors.gbrand500};
  }
`;

export const ModalButtonCall = styled.div(
  ({ theme, typeMargin, page }) => css`
    ${theme.textTypes['Body-2-w400']}
    width: 100%;
    max-width: 274;
    border-radius: 4px;
    text-align: center;
    cursor: pointer;
    padding-top: 9px;
    padding-bottom: 8px;
    background-color: ${theme.colors.white};
    color: ${theme.colors.gbrand500};
    margin-bottom: ${typeMargin === 'merchant' || page === 3 ? '24px' : '32px'};
    border: solid 2px ${theme.colors.gbrand500};
    justify-content: center;
    transition: all 0.3s ease 0s;
    ${theme.breakpoint('md')} {
      display: ${(props) => (props.isMerchant || page === 3 ? 'flex' : 'none')};
      margin-bottom: ${typeMargin === 'merchant' || page === 3 ? '24px' : '32px'};
    }
    &:hover {
      box-shadow: 0 1px 6px rgb(32 33 36 / 28%);
      color: ${theme.colors.gbrand600};
    }
  `
);

const ModalButtonCallLarge = styled.div(
  ({ theme, page }) => css`
    display: none;
    ${theme.breakpoint('md')} {
      margin-bottom: 24px;
      display: ${(props) => (props.isMerchant || page === 3 ? 'none' : 'flex')};
      text-align: center;
      ${theme.textTypes['Subtitle-1-w400']}
      color: ${theme.colors.gray800};
      line-height: 1.5;
      margin-top: 8px;
    }
  `
);

export const ModalTextWhatsApp = styled.div(
  ({ theme }) => css`
    ${theme.textTypes['Caption-w400']}
    line-height: 1.5;
    color: ${theme.colors.gray800};
    text-align: center;
    margin-top: 0px;
    padding-left: 2px;
    padding-right: 2px;
    margin-bottom: 24px;
    transition: all 0.3s ease 0s;
    a {
      color: ${theme.colors.gbrand500};
      text-decoration: none;
      &:hover {
        color: ${theme.colors.gbrand600};
      }
    }
  `
);

const HelpModalInterior = ({
  id,
  handleAskQuestions,
  isMerchant,
  linkWhatsApp,
  linkMail,
  page,
}) => {
  const { t } = useTranslation();
  const { isSeller } = useAuth();
  const { contactUs, contactEmail } = useSelector((state) => state.common);

  const ButtonCall = ({ tel, label, handleMail }) => (
    <ModalButtonCall
      isMerchant={isMerchant}
      page={page}
      type="outlined"
      typeMargin={!isSeller() ? 'merchant' : ''}
      id={`${id}-button-call`}
      onClick={
        isMerchant || page === 3
          ? handleMail
          : (e) => {
              window.location = tel;
              e.preventDefault();
            }
      }
    >
      {label}
    </ModalButtonCall>
  );
  return (
    <ModalContainer id={id}>
      <ModalImage src={`${publicPath}/helpimage.svg`} alt="Ayuda" />
      <ModalTextHeader id={`${id}-header`}>{t('helpModal.modal.header')}</ModalTextHeader>
      <Line />
      <ModalTextBody id={`${id}-text`}>{t('helpModal.modal.text')}</ModalTextBody>
      <ModalButtonQuestions id={`${id}-button-ask-questions`} onClick={handleAskQuestions}>
        {t('helpModal.modal.button1')}
      </ModalButtonQuestions>
      <ButtonCall
        label={
          !isMerchant && page !== 3
            ? t('helpModal.modal.button2', { contactUs: `${contactUs}`.replace('+56 2', '') })
            : t('helpModal.modal.mail', { contactMail: `${contactEmail}` })
        }
        tel={`tel:${contactUs}`}
        handleMail={linkMail}
      />
      <ModalButtonCallLarge
        id={`${id}-button-contactUs`}
        type="link"
        isMerchant={isMerchant}
        page={page}
      >
        {t('helpModal.modal.button3', { contactUs: `${contactUs}`.replace('+56 2', '') })}
      </ModalButtonCallLarge>
      {!isSeller() || page === 3 ? (
        <ModalTextWhatsApp>
          {t('helpModal.modal.textWhatsApp')}
          <a id={`${id}-link-whatsApp`} href="#" onClick={linkWhatsApp}>
            {t('helpModal.modal.whatsApp')}
          </a>
        </ModalTextWhatsApp>
      ) : (
        ''
      )}
    </ModalContainer>
  );
};

HelpModalInterior.prototype = {
  id: PropTypes.string,
  handleAskQuestions: PropTypes.func,
  isMerchant: PropTypes.bool,
  linkWhatsApp: PropTypes.func,
  linkMail: PropTypes.func,
};

HelpModalInterior.defaultProps = {
  id: 'HelpModalInterior',
  handleAskQuestions: undefined,
  isMerchant: undefined,
  linkWhatsApp: undefined,
  linkMail: undefined,
};

export default HelpModalInterior;
